import isInView from "../lib/isInView";
import passiveIfSupported from "../lib/passiveIfSupported";
import Debouncer from '../lib/Debouncer';
import LatLng = google.maps.LatLng;
import InfoWindow = google.maps.InfoWindow;
import * as $ from "jquery";

declare function require(name:string): any;
const MarkerClusterer = require('@google/markerclustererplus');

declare global {
	interface Window {
		refreshMap: () => void;
	}
}

type MapControllerState = {
	map: google.maps.Map,
	markers: google.maps.Marker[],
	baseCenter: google.maps.LatLng,
	infoWindows: google.maps.InfoWindow[],
	thingsToClear: any[],
	fresh: boolean,
	reset: () => void,
	baseBounds: google.maps.LatLngBounds,
	unshiftedBounds: google.maps.LatLngBounds
};
type AdditionalMapControllerOptions = {
	autoRefreshBounds?: boolean,
	markerIconRefresher?: (state: MapControllerState) => (marker: google.maps.Marker) => void,
	boundsApplier?: (state: MapControllerState) => void,
	maximumZoom?: number
};

type MarkerProvider = (state: MapControllerState, panBack: () => void) => google.maps.Marker[];

type InfoWindowListenerMaker = (state: MapControllerState, marker: google.maps.Marker, contentString: string, panBack: () => void) => (() => google.maps.InfoWindow);

type BoundsComputer = (state: MapControllerState) => google.maps.LatLngBounds;

const DEFAULT_BOUNDS_COMPUTER: BoundsComputer = state => {
	const bounds = new google.maps.LatLngBounds();
	state.markers.forEach(marker => bounds.extend(marker.getPosition()));
	return bounds;
};

const pinSizes = [5,7.5,10,15,25,27.5,30,35,25,25,25,25,25,25,25,35,35];
const updateMarkerSize = (state: MapControllerState) => (marker: google.maps.Marker) => {
	let scale = state.map.getZoom()
	if (scale >= pinSizes.length) {
		scale = pinSizes.length - 1;
	}
	marker.setIcon({
			url: (marker.getIcon() as any).url,
			anchor: new google.maps.Point(pinSizes[scale]/2, pinSizes[scale]/2),
			scaledSize: new google.maps.Size(pinSizes[scale], pinSizes[scale])
		}
	);
};
const updateMarkerSizes = (state: MapControllerState) => {
	state.markers.forEach(updateMarkerSize(state));
};

(window as any).googleMapScriptLoaded = () => {};

const mapController = (applicabilityTest: () => boolean, markerProvider: MarkerProvider, boundsComputer: BoundsComputer = DEFAULT_BOUNDS_COMPUTER,
                       options: AdditionalMapControllerOptions = {}) => {
	if (!applicabilityTest()) {
		return {};
	}
	if (!options) {
		options = {};
	}

	const state: MapControllerState = {
		map: null,
		markers: null,
		baseCenter: null,
		infoWindows: [],
		thingsToClear: [],
		fresh: true,
		reset: function () {
			doCloseInfoWindows(this.infoWindows, true);
			doClearMarkers(this.markers);
			this.map = null;
			this.markers = null;
			this.baseCenter = null;
			this.unshiftedBounds = null;
			this.infoWindows = [];
			if (!!this.thingsToClear) {
				this.thingsToClear.forEach((thingToClear: any) => {
					thingToClear.setMap(null);
				});
			}
			this.thingsToClear = [];
			state.fresh = true;
		},
		baseBounds: null,
		unshiftedBounds: null
	};
	const panBack = () => {
		if (state.baseCenter !== null && state.infoWindows.length === 0) {
			state.map.panTo(state.baseCenter);
			state.baseCenter = null;
			state.unshiftedBounds = null;
		}
	};

	let actuallyFirstCall = true;
	const initMap = () => {
		if (typeof google === 'undefined' || typeof (window as any).google === 'undefined') {
			return;
		}
		if (actuallyFirstCall) {
			document.addEventListener('jsfAjaxEvent', evt => {
				if ((evt as any).detail.fireType === 'always') {
					if (document.body.querySelector('#map:not(.map-set-up)') !== null) {
						state.reset();
						initMap();
					} else if (!!document.body.querySelector('.map-needs-updating')) {
						doCloseInfoWindows(state.infoWindows, true);
						doClearMarkers(state.markers);
						panBack();
						initMap();
					}
				}
			});
			window.refreshMap = () => {
				doCloseInfoWindows(state.infoWindows, true);
				doClearMarkers(state.markers);
				panBack();
				initMap();
			}
			(window as any).refreshMarkerIcons = !options.markerIconRefresher
				? () => {}
				: () => {
					ensureMapMarkerOptionIconsAreLoaded();
					state.markers.forEach(options.markerIconRefresher(state));
				};
			actuallyFirstCall = false;
		}
		const mapNeedsUpdatingIndicator = document.body.querySelector('.map-needs-updating');
		if (!!mapNeedsUpdatingIndicator) {
			mapNeedsUpdatingIndicator.classList.remove('map-needs-updating');
		}

		doClearMarkers(state.markers);

		if (state.map === null) {
			const mapElement = document.getElementById('map');
			mapElement.classList.add('map-set-up'); // This is used to account for AJAX requests that replace the map

			state.map = new google.maps.Map(mapElement, {
				zoom: 5,
				maxZoom: 16.5,
				minZoom: 3,
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
				zoomControl: true
			});
		}

		ensureMapMarkerOptionIconsAreLoaded();
		state.markers = markerProvider(state, panBack);

		const updatingBounds = state.baseBounds === null || options.autoRefreshBounds !== false;
		if (updatingBounds) {
			const bounds = boundsComputer(state);
			state.markers.forEach(marker => marker.setMap(state.map));
			state.map.setCenter(bounds.getCenter());
			state.map.fitBounds(bounds);
			state.baseBounds = bounds;
		} else {
			state.markers.forEach(marker => marker.setMap(state.map));
		}
		// This listener ensures that the map's zoom is set correctly.
		const listener = google.maps.event.addListener(state.map, "idle", () => {
			if (state.map.getZoom() > (options.maximumZoom ?? 15)) state.map.setZoom(options.maximumZoom ?? 15);
			google.maps.event.removeListener(listener);
			state.fresh = false;
		});
		google.maps.event.addListener(state.map, "zoom_changed", () => updateMarkerSizes(state));
		if (!!options.boundsApplier) {
			const boundsApplicationDebouncer = new Debouncer(() => {
				options.boundsApplier(state);
				const viewportCheckers = (window as any).viewportCheckers;
				if (viewportCheckers !== undefined && viewportCheckers !== null) {
					for (let i = 0; i < viewportCheckers.length; i++) {
						viewportCheckers[i].check();
					}
				}
			}, false, 250);
			google.maps.event.addListener(state.map, "zoom_changed", boundsApplicationDebouncer.run);
			google.maps.event.addListener(state.map, "bounds_changed", boundsApplicationDebouncer.run);
			google.maps.event.addListener(state.map, "dragend", boundsApplicationDebouncer.runNow);
			google.maps.event.addListener(state.map, "resize", boundsApplicationDebouncer.run);
		}
	};


	const initMapIfReady = () => {
		if (isGoogleMapScriptLoaded && hasMapBeenInView) {
			initMap();
		}
	};

	let isGoogleMapScriptLoaded = false;
	(window as any).googleMapScriptLoaded = () => {
		isGoogleMapScriptLoaded = true;
		initMapIfReady();
	};

	let hasMapBeenInView = false;
	const mapIsInView = () => {
		hasMapBeenInView = true;
		initMapIfReady();
	};
	const onScroll = (ev: Event) => {
		const mapElement = document.getElementById('map');
		const _isInView = isInView(mapElement);
		if (_isInView === true) {
			window.removeEventListener("scroll", debouncedOnScroll.run, passiveIfSupported);
			window.removeEventListener("resize", debouncedOnScroll.run, passiveIfSupported);
			mapIsInView();
		}
	};
	const debouncedOnScroll = new Debouncer(onScroll);
	window.addEventListener("scroll", debouncedOnScroll.run, passiveIfSupported);
	window.addEventListener("resize", debouncedOnScroll.run, passiveIfSupported);
	debouncedOnScroll.runNow();
	document.addEventListener('load', debouncedOnScroll.runNow);


	document.addEventListener('click', function (event) {
		let parent: HTMLElement = event.target as HTMLElement;
		do {
			if (parent.style.zIndex === '202') {
				event.stopPropagation();
				return;
			}
		} while ((parent = (parent.parentElement ?? (parent.parentNode as HTMLElement))) !== null && (parent.id === null || parent.id === ''));

		doCloseInfoWindows(state.infoWindows, false);
		panBack();
	});
	document.addEventListener('keyup', function (event) {
		if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
			doCloseInfoWindows(state.infoWindows, true);
			panBack();
		}
	});
};
const setBaseCenterIfNeeded = (state: MapControllerState, baseCenter: google.maps.LatLng) => {
	if (state.baseCenter === null) {
		state.baseCenter = baseCenter;
		state.unshiftedBounds = state.map.getBounds();
	}
};
const doClearMarkers = (markers: google.maps.Marker[]) => {
	if (!!markers) {
		markers.forEach(marker => {
			const onRemoveAction: () => void = marker.get('onRemoveAction');
			marker.setMap(null);
			if (typeof onRemoveAction === 'function') {
				onRemoveAction();
			}
		});
	}
};
const doCloseInfoWindows = (infoWindows: google.maps.InfoWindow[], ignoreCanBeRemoved: boolean) => {
	if (!!infoWindows) {
		let min = 0;
		while (infoWindows.length > min) {
			if (ignoreCanBeRemoved || (infoWindows[infoWindows.length - 1] as any).canBeRemoved) {
				const infoWindow = infoWindows.pop();
				(infoWindow.get('builtInOnClose') as (() => void))();
				const onCloseAction: () => void = infoWindow.get('onCloseAction');
				infoWindow.close();
				if (typeof onCloseAction === 'function') {
					onCloseAction();
				}
			} else {
				min++;
				(infoWindows[infoWindows.length - 1] as any).canBeRemoved = true;
			}
		}
	}
};
const makeInfoWindowListener: InfoWindowListenerMaker = (state, marker, contentString, panBack) => {
	return function () {
		setBaseCenterIfNeeded(state, state.map.getCenter());
		doCloseInfoWindows(state.infoWindows, true);
		const infoWindow = new google.maps.InfoWindow({
			content: contentString,
			zIndex: 202
		});
		infoWindow.open(state.map, marker);
		marker.set('selected', true);
		const baseMarkerType = marker.get('markerType') ?? 'default';
		marker.setIcon((mapMarkerOptions[baseMarkerType + '-selected'] ?? mapMarkerOptions[baseMarkerType] ?? mapMarkerOptions['default']).icon);
		updateMarkerSize(state)(marker);
		(infoWindow as any).canBeRemoved = false;
		state.infoWindows.push(infoWindow);
		infoWindow.set('builtInOnClose', () => {
			marker.set('selected', undefined);
			marker.setIcon((mapMarkerOptions[baseMarkerType] ?? mapMarkerOptions['default']).icon);
			updateMarkerSize(state)(marker);
		})
		infoWindow.addListener('closeclick', () => {
			doCloseInfoWindows(state.infoWindows, true);
			panBack();
		});
		return infoWindow
	};
};
const mapMarkerOptions: any = {
	'default': { zIndex: 0, fill: '00807B' },
	'default-saved': { zIndex: 0, fill: '00807B' },
	'default-compared': { zIndex: 0, fill: '00807B' },
	'default-saved-compared': { zIndex: 0, fill: '00807B' },
	'default-hovered': { zIndex: 0, fill: '00AEA9' },
	'default-saved-hovered': { zIndex: 0, fill: '00AEA9' },
	'default-compared-hovered': { zIndex: 0, fill: '00AEA9' },
	'default-saved-compared-hovered': { zIndex: 0, fill: '00AEA9' },
	'default-selected': { zIndex: 0, fill: 'CC4E05' },
	'default-saved-selected': { zIndex: 0, fill: 'CC4E05' },
	'default-compared-selected': { zIndex: 0, fill: 'CC4E05' },
	'default-saved-compared-selected': { zIndex: 0, fill: 'CC4E05' },
	'home': { zIndex: 999, fill: '1953A5' },

	'entertainment': { zIndex: 10, fill: '18A6F8', icon: {url: document.getElementsByTagName('base')[0].href + 'assets/graphics/map-entertainment.png'}},
	'food': { zIndex: 10, fill: '18A6F8', icon: {url: document.getElementsByTagName('base')[0].href + 'assets/graphics/map-food.png'}},
	'shops-service': { zIndex: 10, fill: '18A6F8', icon: {url: document.getElementsByTagName('base')[0].href + 'assets/graphics/map-shopping.png'}},
	'parks-recreation': { zIndex: 10, fill: '18A6F8', icon: {url: document.getElementsByTagName('base')[0].href + 'assets/graphics/map-parks.png'}},
	'education': { zIndex: 10, fill: '18A6F8', icon: {url: document.getElementsByTagName('base')[0].href + 'assets/graphics/map-education.png'}}
};
const ensureMapMarkerOptionIconsAreLoaded = () => {
	for (let mapMarkerOptionsKey in mapMarkerOptions) {
		if (mapMarkerOptions.hasOwnProperty(mapMarkerOptionsKey)) {
			const data = mapMarkerOptions[mapMarkerOptionsKey];
			if (!data['icon']) {
				let paths = '<path d="M35.244,48.175C37.376,27.937,49.806,28.8,49.806,15.528a15.528,15.528,0,0,0-31.056,0c0,13.355,12.428,12.426,14.562,32.647a1.01,1.01,0,0,0,1.931,0Z" transform="translate(298.25 712)" fill="#' + data.fill + '" stroke="#fff" stroke-width="2"> </path>';
				if (mapMarkerOptionsKey.indexOf('-saved') >= 0) {
					paths = paths + '<g transform="translate(0 146)">' +
						'<g transform="matrix(1, 0, 0, 1, 305.5, 559.5)" filter="url(#Ellipse_16)">' +
						'<circle cx="11" cy="11" r="11" transform="translate(30.5 4.5)" fill="#fff"> </circle>' +
						'</g>' +
						'<path d="M12.125,3.882a3.015,3.015,0,0,0-4.265,0l-.581.581L6.7,3.882A3.016,3.016,0,0,0,2.432,8.147l.581.581,4.265,4.265,4.265-4.265.581-.581a3.015,3.015,0,0,0,0-4.265Z" transform="translate(339.451 567.002)" fill="#cc4e05"> </path>' +
						'</g>';
				}
				if (mapMarkerOptionsKey.indexOf('-compared') >= 0) {
					paths = paths + '<g transform="translate(-12 81)">' +
						'<g transform="matrix(1, 0, 0, 1, 317.5, 624.5)" filter="url(#Ellipse_59)">' +
						'<circle cx="11" cy="11" r="11" transform="translate(4.5 20.5)" fill="#fff"/>' +
						'</g>' +
						'<path d="M5.994,15.738,1.52,11.264,3.332,9.452,6.038,12.08l6.295-6.3L14.1,7.63Z" transform="translate(325.48 645.883)" fill="#cc4e05"/>' +
						'</g>';
				}
				data['icon'] = {
					url: 'data:image/svg+xml;base64, ' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="57" height="56.389" viewBox="0 0 57 56.389">' +
						'<defs>' +
						'<filter id="Ellipse_16" x="26" y="0" width="31" height="31" filterUnits="userSpaceOnUse">' +
						'<feOffset input="SourceAlpha"/>' +
						'<feGaussianBlur stdDeviation="1.5" result="blur"/>' +
						'<feFlood flood-opacity="0.329"/>' +
						'<feComposite operator="in" in2="blur"/>' +
						'<feComposite in="SourceGraphic"/>' +
						'</filter>' +
						'<filter id="Ellipse_59" x="0" y="16" width="31" height="31" filterUnits="userSpaceOnUse">' +
						'<feOffset input="SourceAlpha"/>' +
						'<feGaussianBlur stdDeviation="1.5" result="blur-2"/>' +
						'<feFlood flood-opacity="0.329"/>' +
						'<feComposite operator="in" in2="blur-2"/>' +
						'<feComposite in="SourceGraphic"/>' +
						'</filter>' +
						'</defs><g transform="translate(-305.5 -705.5)">' + paths + '</g></svg>'),
					anchor: new google.maps.Point(12.5, 12.5),
					scaledSize: new google.maps.Size(25, 25)
				}
			}
		}
	}
}
const addMarker = (state: MapControllerState, newMarkers: google.maps.Marker[], markerType: string, lat: any, lon: any, contentString: string, panBack: () => void,
                   onClickListenerProducer: (marker: google.maps.Marker) => () => void,
                   infoWindowListenerMaker: InfoWindowListenerMaker = makeInfoWindowListener): google.maps.Marker => {
	const marker = new google.maps.Marker({
		position: new google.maps.LatLng(Number(lat), Number(lon)),
		icon: (mapMarkerOptions[markerType] ?? mapMarkerOptions['default']).icon,
		zIndex: (mapMarkerOptions[markerType] ?? mapMarkerOptions['default']).zIndex,
		optimized: false
	});
	marker.set('markerType', markerType);
	if (infoWindowListenerMaker !== null) {
		marker.addListener('click', infoWindowListenerMaker(state, marker, contentString, panBack));
	}
	if (onClickListenerProducer !== null) {
		marker.addListener('click', onClickListenerProducer(marker));
	}
	newMarkers.push(marker);
	return marker;
};

const mapControllerInitializer = () => {
	{
		const markerTypeDeterminer: (element: Element) => string = element => {
			let markerType = (element.getAttribute('data-marker-type') ?? 'default').toLowerCase();
			if (element.getAttribute('data-property-is-saved') === 'true') {
				markerType += '-saved';
			}
			if (element.getAttribute('data-property-is-compared') === 'true') {
				markerType += '-compared';
			}
			return markerType;
		};
		let lmMarkerClusterer: any = null;
		mapController(() => {
			if (document.querySelector('.propertyListing__items') !== null) {
				document.addEventListener('jsfAjaxEvent', evt => {
					if ((evt as any).detail.fireType === 'success') {
						const state = (window as any).propertyListingMapState;
						if (!!state) {
							if (!!state.thingsToClear) {
								state.thingsToClear.forEach((thingToClear: any) => {
									thingToClear.setMap(null);
								});
							}
							state.thingsToClear = [];
							state.fresh = true;
						}
					}
				});
				return true;
			}
			return false;
		}, (state, panBack) => {
			(window as any).propertyListingMapState = state;
			if (!lmMarkerClusterer || lmMarkerClusterer.getMap() !== state.map) {
				const markerClustererStyles = {
					gridSize: 26,
					styles: [{
						url: 'assets/pins/group.svg',
						height: 26,
						width: 26,
						anchor: [0, 0],
						textColor: '#ffffff',
						textSize: 10
					}]
				};
				lmMarkerClusterer = new MarkerClusterer(state.map, [], markerClustererStyles);
				lmMarkerClusterer.setMaxZoom(15);
			} else {
				lmMarkerClusterer.clearMarkers();
			}

			if (document.querySelector('.filterless-bar.activeState') === null) {
				const circleDataElement = document.getElementById('searchCircleLocation');
				if (circleDataElement.getAttribute('data-center-lat') !== null && '' !== circleDataElement.getAttribute('data-center-lat')) {
					state.thingsToClear.push(new google.maps.Circle({
						strokeColor: '#00aea9',
						strokeOpacity: 1,
						fillColor: '#4979ad',
						fillOpacity: 0.15,
						center: {lat: +circleDataElement.getAttribute('data-center-lat'), lng: +circleDataElement.getAttribute('data-center-lon')},
						radius: +circleDataElement.getAttribute('data-center-radius'),
						map: state.map
					}));
				}
			}
			const results = document.querySelectorAll('.propertyListing__items .propertyCard');
			let newMarkers: google.maps.Marker[] = [];
			let lastClickedMarker: google.maps.Marker = null;
			const infoWindowListenerMaker: InfoWindowListenerMaker = (state: MapControllerState, marker: google.maps.Marker, contentString: string, panBack: () => void) => {
				const madeInfoWindowListener: () => InfoWindow = makeInfoWindowListener(state, marker, contentString, panBack);
				return () => {
					if (lastClickedMarker !== null) {
						if (lastClickedMarker === marker) {
							let destination = lastClickedMarker.get('destination');
							if (destination instanceof HTMLAnchorElement) {
								destination.click();
							} else {
								document.location.href = destination;
							}
						} else {
							lastClickedMarker.get('sourceElement').classList.remove('isMarkerActive');
						}
					}
					lastClickedMarker = marker;
					marker.get('sourceElement').classList.add('isMarkerActive');
					marker.get('sourceElement').scrollIntoView({block: 'center', inline: 'end', behavior: 'smooth'});

					const infoWindow = madeInfoWindowListener();
					infoWindow.set('onCloseAction', () => {
						if (lastClickedMarker === marker) {
							lastClickedMarker.get('sourceElement').classList.remove('isMarkerActive');
							lastClickedMarker = null;
						}
					});
					return infoWindow;
				}
			}

			for (let i = 0; i < results.length; i++) {
				const result = results.item(i);
				const propertyId = result.getAttribute('data-property-id');
				const primaryLink = result.querySelector('.primaryLink');
				const contentString =
					'<div class="mapToolTip" data-property-id="' + propertyId + '"><a href="' + primaryLink.getAttribute('href') + '" class="ajaxPageLink">' +
					'<div>' +
					'<div class="mapToolTip__heading">' + primaryLink.textContent + '</div>' +
					'</div>' +
					'</a></div>';
				if (result.getAttribute('data-lat') !== null && result.getAttribute('data-lon') !== null) {
					const markerType = markerTypeDeterminer(result);
					const marker = addMarker(
						state, newMarkers, markerType,
						result.getAttribute('data-lat'),
						result.getAttribute('data-lon'),
						contentString, panBack, null, infoWindowListenerMaker
					);
					marker.set('destination', primaryLink);
					lmMarkerClusterer.addMarker(marker);

					marker.set('sourceElement', result);
					const onOver = () => {
						result.classList.add('isMarkerHover');
						marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default') + (!!marker.get('selected') ? '-selected' : '-hovered')].icon);
						updateMarkerSize(state)(marker);
					};
					const onOut = () => {
						result.classList.remove('isMarkerHover');
						result.classList.remove('isMouseDown');
						marker.setIcon(mapMarkerOptions[(marker.get('markerType') ?? 'default') + (!!marker.get('selected') ? '-selected' : '')].icon);
						updateMarkerSize(state)(marker);
					};
					const onMouseDown = () => {
						result.classList.add('isMouseDown');
					};
					const onMouseUp = () => {
						result.classList.remove('isMouseDown');
					};
					marker.addListener('mouseover', onOver);
					marker.addListener('mouseout', onOut);
					result.addEventListener('mouseover', onOver);
					result.addEventListener('mouseout', onOut);
					result.addEventListener('mousedown', onMouseDown);
					result.addEventListener('mouseup', onMouseUp);

					marker.set('onRemoveAction', () => {
						onOut();
						result.removeEventListener('mouseover', onOver);
						result.removeEventListener('mouseout', onOut);
						result.removeEventListener('mousedown', onMouseDown);
						result.removeEventListener('mouseup', onMouseUp);
					});
				}
			}
			return newMarkers;
		}, state => {
			const bounds = new google.maps.LatLngBounds();
			if (state.fresh && state.thingsToClear.length > 0) {
				state.thingsToClear.forEach(ttc => bounds.union(ttc.getBounds()));
			} else {
				const listings = document.querySelectorAll('.locationListing .locationList[data-north-east-lat][data-north-east-lon][data-south-west-lat][data-south-west-lon]');
				if (listings.length !== 0) {
					for (let i = 0; i < listings.length; i++) {
						const listing = listings.item(i);
						bounds.extend(new google.maps.LatLng(Number(listing.getAttribute('data-north-east-lat')),
							Number(listing.getAttribute('data-north-east-lon'))));

						bounds.extend(new google.maps.LatLng(Number(listing.getAttribute('data-south-west-lat')),
							Number(listing.getAttribute('data-south-west-lon'))));
						if (listing.getAttribute('data-incorporate-results') === 'true') {
							const center = bounds.getCenter();
							// This mirrors every added point so that the viewport remains on the original center
							for (let j = 0, limit = state.markers.length < 5 ? state.markers.length : 5; j < limit; j++) {
								const marker: LatLng = state.markers[j].getPosition();
								let deltaLat = marker.lat() - center.lat();
								if (deltaLat < 0) {
									deltaLat *= -1;
								}

								let deltaLng = marker.lng() - center.lng();
								if (deltaLng < 0) {
									deltaLng *= -1;
								}

								bounds.extend(new google.maps.LatLng(center.lat() + deltaLat, center.lng() + deltaLng));
								bounds.extend(new google.maps.LatLng(center.lat() - deltaLat, center.lng() - deltaLng));
							}
						}
					}
				} else {
					return DEFAULT_BOUNDS_COMPUTER(state);
				}
			}
			return bounds;
		}, {
			markerIconRefresher: state => marker => {
				const sourceElement: HTMLElement = marker.get('sourceElement');
				if (sourceElement !== null) {
					const markerType = markerTypeDeterminer(sourceElement);
					marker.set('markerType', markerType);
					if (!!marker.get('selected')) {
						marker.setIcon(mapMarkerOptions[(markerType || 'default') + '-selected'].icon);
					} else {
						if (sourceElement.classList.contains('isMarkerHover')) {
							marker.setIcon(mapMarkerOptions[(markerType || 'default') + '-hovered'].icon);
						} else {
							marker.setIcon(mapMarkerOptions[(markerType || 'default')].icon);
						}
					}
					updateMarkerSize(state)(marker);
				}
			},
			boundsApplier: state => {
				const bounds = state.unshiftedBounds ?? state.map.getBounds();
				const resultCountElements: HTMLElement[] = [];
				document.querySelectorAll('.resultCount[data-base-count]').forEach(ele => resultCountElements.push(ele as HTMLElement));
				if (resultCountElements.length > 0) {
					let totalHidden = 0;
					for (let i = 0; i < state.markers.length; i++) {
						let marker = state.markers[i];
						if (bounds.contains(marker.getPosition())) {
							marker.get('sourceElement').style.display = '';
						} else {
							totalHidden++;
							marker.get('sourceElement').style.display = 'none';
						}
					}
					resultCountElements.forEach(ele => {
						ele.innerText = (parseInt(ele.getAttribute('data-base-count')) - totalHidden).toString();
					});
				} else {
					for (let i = 0; i < state.markers.length; i++) {
						let marker = state.markers[i];
						marker.get('sourceElement').style.display = bounds.contains(marker.getPosition()) ? '' : 'none';
					}
				}
			}
		});
	}
	{
		mapController(() => {
			if (document.querySelector('.poi-type-selector') !== null) {
				document.addEventListener('click', event => {
					const target = event.target;
					if (target instanceof HTMLElement && target.classList.contains('poi-type') && !target.classList.contains('selected')) {
						document.querySelector('.poi-type.selected').classList.remove('selected');
						target.classList.add('selected');
						window.refreshMap();
					}
				});
				return true;
			}
			return false;
		}, (state, panBack) => {
			let newMarkers: google.maps.Marker[] = [];
			(document.querySelector('.map-set-up') as HTMLElement).style.height = '500px';
			(document.querySelector('.map-set-up') as HTMLElement).style.width = '500px';

			const mainAddress = document.querySelector('[itemprop="address"][data-lat][data-lon]');
			const homeContentString =
				'<div class="mapToolTip">' +
					'<div>' +
						'<div class="mapToolTip__heading">' +
							(document.querySelector('.property__name span') ?? document.querySelector('.propertySimple__name span')).textContent +
						'</div>' +
					'</div>' +
				'</div>';
			addMarker(state, newMarkers, 'home',
				Number(mainAddress.getAttribute('data-lat')), Number(mainAddress.getAttribute('data-lon')),
				homeContentString, panBack, null
			);
			const selectedMarker = document.querySelector('li.poi-type.selected').getAttribute('data-poi-type');
			const nearbyPlaces: any[] = (window as any).nearbyPlaces;
			if (nearbyPlaces !== undefined) {
				for (let i = 0; i < nearbyPlaces.length; i++) {
					const nearbyPlace = nearbyPlaces[i];
					if (selectedMarker === 'show-all' || selectedMarker === nearbyPlace.category) {
						const contentString =
							'<div class="mapToolTip">' +
							'<div>' +
							'<div class="mapToolTip__heading">' + nearbyPlace.name + '</div>' +
							'</div>' +
							'</div>';
						addMarker(state, newMarkers, nearbyPlace.category, nearbyPlace.lat, nearbyPlace.lon,
							contentString, panBack, null
						);
					}
					(document.querySelector('.poi-type[data-poi-type="' + nearbyPlace.category + '"]') as HTMLElement).style.display = null;
				}
				(document.querySelector('.poi-type-selector') as HTMLElement).style.display = null;
			}
			return newMarkers;
		}, state => {
			const bounds = new google.maps.LatLngBounds();
			const poiTypeSelectors = document.querySelectorAll('.poi-type-selector[data-north-east-lat][data-north-east-lon][data-south-west-lat][data-south-west-lon]');
			if (poiTypeSelectors.length !== 0) {
				for (let i = 0; i < poiTypeSelectors.length; i++) {
					const listing = poiTypeSelectors.item(i);
					bounds.extend(new google.maps.LatLng(Number(listing.getAttribute('data-north-east-lat')),
						Number(listing.getAttribute('data-north-east-lon'))));

					bounds.extend(new google.maps.LatLng(Number(listing.getAttribute('data-south-west-lat')),
						Number(listing.getAttribute('data-south-west-lon'))));
					if (listing.getAttribute('data-incorporate-results') === 'true') {
						const center = bounds.getCenter();
						// This mirrors every added point so that the viewport remains on the original center
						for (let j = 0, limit = state.markers.length < 5 ? state.markers.length : 5; j < limit; j++) {
							const marker: LatLng = state.markers[j].getPosition();
							let deltaLat = marker.lat() - center.lat();
							if (deltaLat < 0) {
								deltaLat *= -1;
							}

							let deltaLng = marker.lng() - center.lng();
							if (deltaLng < 0) {
								deltaLng *= -1;
							}

							bounds.extend(new google.maps.LatLng(center.lat() + deltaLat, center.lng() + deltaLng));
							bounds.extend(new google.maps.LatLng(center.lat() - deltaLat, center.lng() - deltaLng));
						}
					}
				}
			} else {
				bounds.extend(new google.maps.LatLng(state.markers[0].getPosition().lat() + 0.0005, state.markers[0].getPosition().lng()));
				const center = bounds.getCenter();
				// This mirrors every added point so that the viewport remains on the original center
				for (let j = 1, limit = state.markers.length; j < limit; j++) {
					const marker: LatLng = state.markers[j].getPosition();
					let deltaLat = marker.lat() - center.lat();
					if (deltaLat < 0) {
						deltaLat *= -1;
					}

					let deltaLng = marker.lng() - center.lng();
					if (deltaLng < 0) {
						deltaLng *= -1;
					}

					bounds.extend(new google.maps.LatLng(center.lat() + deltaLat, center.lng() + deltaLng));
					bounds.extend(new google.maps.LatLng(center.lat() - deltaLat, center.lng() - deltaLng));
				}
			}
			return bounds;
		}, {autoRefreshBounds: false, maximumZoom: 16});
	}
};

export default mapControllerInitializer;
